import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Btn, Field, inputCls, Modal, EmptyState, money, TierPill } from "@/components/crm/kit";
import { ArrowUp, ArrowDown, ChevronsUpDown, UserPlus, Users2, ChevronLeft, ChevronRight, Filter, Pill } from "lucide-react";

const canon = (text) => {
  const t = (text || "").trim();
  if (!t) return {};
  if (/^\d{5}$/.test(t)) return { origin_zip: t };
  const m = t.match(/^(.+),\s*([A-Za-z]{2})$/);
  if (m) return { origin_city: m[1].trim(), origin_state: m[2].trim() };
  return { origin_address: t };
};

const PAGE = 25;

export default function ReferralInfluence({ originInput, radiusMi, gen, loadedId, users = [], onChanged }) {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [hospitals, setHospitals] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [sort, setSort] = useState({ col: "tot_cost", dir: "desc" });
  const [hosp, setHosp] = useState("");
  const [src, setSrc] = useState("all");
  const [minCost, setMinCost] = useState("");
  const [minClaims, setMinClaims] = useState("");
  const [costQ, setCostQ] = useState("");
  const [claimsQ, setClaimsQ] = useState("");
  const [trunc, setTrunc] = useState(false);
  const [handoff, setHandoff] = useState(null);

  const fetchRows = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const payload = {
        ...canon(originInput), radius_mi: radiusMi,
        specialty: gen.specialty || undefined, tiers: gen.tiers, tier_rpm: gen.tier_rpm || undefined,
        hospital_system: hosp || undefined, source: src,
        min_cost: costQ ? Number(costQ) : undefined,
        min_claims: claimsQ ? Number(claimsQ) : undefined,
        sort: sort.col, sort_dir: sort.dir, page: p, page_size: PAGE,
      };
      const r = await api.post("/medicare/referral-influence", payload);
      setRows(r.data.results || []);
      setTotal(r.data.total || 0);
      setHospitals(r.data.hospital_systems || []);
      setTrunc(!!r.data.medicare_truncated);
      setPage(p);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [originInput, radiusMi, gen, hosp, src, costQ, claimsQ, sort]);

  useEffect(() => { fetchRows(1); }, [fetchRows]);
  useEffect(() => {
    const t = setTimeout(() => { setCostQ(minCost); setClaimsQ(minClaims); }, 500);
    return () => clearTimeout(t);
  }, [minCost, minClaims]);

  const toggleSort = (col) =>
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "desc" }));

  const openHandoff = (row) =>
    setHandoff({ row, owner_id: users[0]?.id || "", create_task: true, due_at: "", note: "" });

  const submitHandoff = async () => {
    const h = handoff;
    if (!h.owner_id) return toast.error("Pick an account manager / closer");
    try {
      const r = await api.post("/crm/referral-influence/handoff", {
        lead_id: h.row.lead_id || undefined, npi: h.row.npi || undefined,
        first_name: h.row.first_name, last_name: h.row.last_name, specialty: h.row.specialty,
        city: h.row.city, state: h.row.state, hospital_system: h.row.hospital_system || undefined,
        owner_id: h.owner_id, create_task: h.create_task, due_at: h.due_at || undefined,
        note: h.note || undefined,
        referral_generator_id: loadedId || undefined, referral_generator_name: gen.name || undefined,
      });
      toast.success(`Handed to owner · lead ${r.data.lead_number}${r.data.task_id ? " + intro task" : ""}`);
      setHandoff(null);
      fetchRows(page);
      onChanged && onChanged();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE));
  const SortTh = ({ col, label, right }) => (
    <th className={`p-3 ${right ? "text-right" : ""}`}>
      <button type="button" data-testid={`ri-sort-${col}`} onClick={() => toggleSort(col)}
        className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider hover:text-primary ${sort.col === col ? "text-primary" : ""}`}>
        {label}
        {sort.col === col ? (sort.dir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />) : <ChevronsUpDown className="h-3 w-3 opacity-40" />}
      </button>
    </th>
  );

  return (
    <div data-testid="referral-influence">
      {/* Filters */}
      <div className="mb-3 flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-white p-3">
        <Filter className="mb-2 h-4 w-4 text-primary" />
        <Field label="Hospital system">
          <select data-testid="ri-hospital" value={hosp} onChange={(e) => setHosp(e.target.value)} className={inputCls}>
            <option value="">All systems</option>
            {hospitals.map((h) => <option key={h} value={h}>{h}</option>)}
          </select>
        </Field>
        <Field label="Source">
          <select data-testid="ri-source" value={src} onChange={(e) => setSrc(e.target.value)} className={inputCls}>
            <option value="all">Medicare + our leads</option>
            <option value="medicare">Medicare only</option>
            <option value="leads">Our leads only</option>
          </select>
        </Field>
        <Field label="Min total drug cost ($)">
          <input data-testid="ri-min-cost" type="number" value={minCost} onChange={(e) => setMinCost(e.target.value)} placeholder="e.g. 250000" className={inputCls} />
        </Field>
        <Field label="Min total claims">
          <input data-testid="ri-min-claims" type="number" value={minClaims} onChange={(e) => setMinClaims(e.target.value)} placeholder="e.g. 1000" className={inputCls} />
        </Field>
      </div>

      {hospitals.length === 0 && (
        <p className="mb-2 text-xs text-slate-400">Tip: the Hospital System column fills in from AI Enrich (web research) on a lead — then you can filter by health system here.</p>
      )}

      <div className="mb-3 flex items-center justify-between">
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Users2 className="h-4 w-4 text-primary" /> {total.toLocaleString()} doctors in radius {gen.name ? `of ${gen.name}` : ""}{trunc && <span className="ml-1 text-xs font-normal text-amber-600">(top 20,000 by drug cost)</span>}</span>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <button disabled={page <= 1} onClick={() => fetchRows(page - 1)} className="rounded p-1 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /></button>
          Page {page} / {totalPages}
          <button disabled={page >= totalPages} onClick={() => fetchRows(page + 1)} className="rounded p-1 disabled:opacity-40"><ChevronRight className="h-4 w-4" /></button>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs text-slate-500">
            <tr>
              <SortTh col="name" label="Physician" />
              <SortTh col="specialty" label="Specialty" />
              <SortTh col="city" label="Location" />
              <SortTh col="hospital_system" label="Hospital System" />
              <SortTh col="source" label="Source" />
              <SortTh col="crm_status" label="CRM" />
              <SortTh col="tot_clms" label="Total Claims" right />
              <SortTh col="tot_cost" label="Drug Cost" right />
              <th className="p-3 text-xs font-bold uppercase tracking-wider">Top Drug</th>
              <th className="p-3 text-xs font-bold uppercase tracking-wider">340B</th>
              <th className="p-3 text-xs font-bold uppercase tracking-wider">RPM</th>
              <SortTh col="distance_mi" label="Dist" right />
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={13} className="p-8 text-center text-slate-400">Loading doctors…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={13}><EmptyState icon={Users2} title="No doctors match" hint="Adjust the radius or filters." /></td></tr>}
            {!loading && rows.map((r, i) => (
              <tr key={`${r.npi || r.lead_id}-${i}`} data-testid={`ri-row-${r.npi || r.lead_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-semibold text-slate-800">
                  {r.lead_id ? (
                    <button type="button" onClick={() => nav(`/crm/leads/${r.lead_id}`)} className="hover:text-primary hover:underline">Dr. {r.first_name} {r.last_name}</button>
                  ) : (<>Dr. {r.first_name} {r.last_name}</>)}
                </td>
                <td className="p-3 text-slate-500">{r.specialty || "—"}</td>
                <td className="p-3 text-slate-500">{r.city}, {r.state}</td>
                <td className="p-3 text-slate-600">{r.hospital_system || <span className="text-slate-300">—</span>}</td>
                <td className="p-3"><span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${r.source === "Medicare" ? "bg-slate-100 text-slate-500" : r.source === "Lead" ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{r.source}</span></td>
                <td className="p-3">{r.crm_status ? <span className="text-xs font-semibold text-slate-600">{r.crm_status}</span> : <span className="text-xs text-slate-300">—</span>}</td>
                <td className="p-3 text-right text-slate-700">{r.tot_clms != null ? Number(r.tot_clms).toLocaleString() : "—"}</td>
                <td className="p-3 text-right font-bold text-slate-800">{r.tot_cost != null ? money(r.tot_cost) : "—"}</td>
                <td className="p-3 text-slate-500">{r.sample_drug ? <span title="Representative drug on record (not full per-drug breakdown)" className="inline-flex items-center gap-1"><Pill className="h-3 w-3 text-slate-400" />{r.sample_drug}</span> : "—"}</td>
                <td className="p-3"><TierPill kind="" tier={r.tier_340b} /></td>
                <td className="p-3"><TierPill kind="" tier={r.tier_rpm} /></td>
                <td className="p-3 text-right text-slate-500">{r.distance_mi != null ? `${r.distance_mi}mi` : "—"}</td>
                <td className="p-3 text-right"><Btn variant="ghost" onClick={() => openHandoff(r)} data-testid={`ri-handoff-${r.npi || r.lead_id}`}><UserPlus className="h-4 w-4" /> Hand off</Btn></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={!!handoff} onClose={() => setHandoff(null)} title="Hand off to account manager / closer">
        {handoff && (
          <div className="space-y-3" data-testid="ri-handoff-modal">
            <p className="text-sm text-slate-600">Dr. {handoff.row.first_name} {handoff.row.last_name} · {handoff.row.city}, {handoff.row.state}{handoff.row.lead_id ? " · already a lead" : " · will be created as a lead"}</p>
            <Field label="Owner (account manager / closer)">
              <select data-testid="ri-handoff-owner" value={handoff.owner_id} onChange={(e) => setHandoff({ ...handoff, owner_id: e.target.value })} className={inputCls}>
                <option value="">Select owner…</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.role})</option>)}
              </select>
            </Field>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" data-testid="ri-handoff-task" checked={handoff.create_task} onChange={(e) => setHandoff({ ...handoff, create_task: e.target.checked })} />
              Create an intro follow-up task for the owner
            </label>
            {handoff.create_task && (
              <Field label="Task due date (optional)"><input type="date" data-testid="ri-handoff-due" value={handoff.due_at} onChange={(e) => setHandoff({ ...handoff, due_at: e.target.value })} className={inputCls} /></Field>
            )}
            <Field label="Note (optional)"><input data-testid="ri-handoff-note" value={handoff.note} onChange={(e) => setHandoff({ ...handoff, note: e.target.value })} placeholder={`Use ${gen.name || "the referral partner"}'s influence to introduce this doctor`} className={inputCls} /></Field>
            <Btn onClick={submitHandoff} data-testid="ri-handoff-submit" className="w-full justify-center"><UserPlus className="h-4 w-4" /> Hand off doctor</Btn>
          </div>
        )}
      </Modal>
    </div>
  );
}
